import type { ExternalProvider } from '@prisma/client';
import { z } from 'zod';
import type { MailboxFolder } from '@/services/channels/contract.js';

export const MAILBOX_PROVIDERS = ['GOOGLE', 'MICROSOFT'] as const;
export type MailboxProviderValue = (typeof MAILBOX_PROVIDERS)[number];
export const MAILBOX_PROVIDERS_MATCH_PRISMA: [ExternalProvider] extends [MailboxProviderValue] ? true : never = true;

const calendarDateSchema = z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/);
const nameListSchema = z.array(z.string().trim().min(1)).max(20);
const positiveIdSchema = z.number().int().positive();

function isIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

const ianaTimeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .refine(isIanaTimeZone, { message: 'timeZone must be an IANA time zone' });

export const mailboxQuerySchema = z
  .object({
    text: z.string().trim().min(1).optional(),
    anyOfText: nameListSchema.optional(),
    from: nameListSchema.optional(),
    to: nameListSchema.optional(),
    subject: z.string().trim().min(1).optional(),
    after: calendarDateSchema.optional(),
    before: calendarDateSchema.optional(),
    hasAttachment: z.boolean().optional(),
    unreadOnly: z.boolean().optional(),
    folders: z
      .object({
        anyOf: nameListSchema.optional(),
        noneOf: nameListSchema.optional(),
      })
      .strict()
      .optional(),
    includeSpamTrash: z.boolean().optional(),
    timeZone: ianaTimeZoneSchema
      .describe("the member's IANA timezone, so after and before mean local midnight; pass the timezone from your context header")
      .optional(),
  })
  .strict();
export type MailboxQueryInput = z.infer<typeof mailboxQuerySchema>;

export const mailboxAccountSchema = z.object({
  id: z.number().int(),
  provider: z.enum(MAILBOX_PROVIDERS),
  externalEmail: z.string(),
  productLabel: z.string(),
});
export type MailboxAccount = z.infer<typeof mailboxAccountSchema>;

export const mailboxAttachmentRefSchema = z.object({
  attachmentId: z.string(),
  filename: z.string().nullable(),
  mimeType: z.string().nullable(),
  size: z.number().int().nullable(),
});
export type MailboxAttachmentRef = z.infer<typeof mailboxAttachmentRefSchema>;

export const mailboxEnvelopeSchema = z.object({
  providerMessageId: z.string(),
  providerThreadId: z.string().nullable(),
  subject: z.string().nullable(),
  from: z.object({ email: z.string().nullable(), name: z.string().nullable() }),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  sentAt: z.string().nullable(),
  receivedAt: z.string().nullable(),
  snippet: z.string().nullable(),
  hasAttachments: z.boolean(),
  attachments: z.array(mailboxAttachmentRefSchema),
  isRead: z.boolean(),
  isDraft: z.boolean(),
  isStarred: z.boolean(),
  folders: z.array(z.string()),
  webLink: z.string().nullable(),
  importedContentId: z.number().int().nullable(),
  importedCode: z.string().nullable(),
});
export type MailboxEnvelope = z.infer<typeof mailboxEnvelopeSchema>;

export const mailboxImportedRefsBodySchema = z
  .object({
    providerMessageIds: z
      .array(z.string().trim().min(1))
      .min(1)
      .max(100)
      .transform((ids) => [...new Set(ids)]),
  })
  .strict();
export type MailboxImportedRefsBody = z.input<typeof mailboxImportedRefsBodySchema>;

export const mailboxImportedRefSchema = z.object({
  providerMessageId: z.string(),
  contentId: z.number().int(),
  code: z.string(),
});
export type MailboxImportedRef = z.infer<typeof mailboxImportedRefSchema>;

export const mailboxImportedRefsOutputSchema = z.object({
  refs: z.array(mailboxImportedRefSchema),
});
export interface MailboxImportedRefsResult {
  refs: MailboxImportedRef[];
}

export const mailboxSearchInputSchema = z
  .object({
    memberExternalAccountId: positiveIdSchema,
    query: mailboxQuerySchema,
    maxResults: z.number().int().min(1).max(100).default(25),
    pageToken: z.string().trim().min(1).optional(),
  })
  .strict();
export type MailboxSearchInput = z.infer<typeof mailboxSearchInputSchema>;

export interface MailboxSearchResult {
  account: MailboxAccount;
  messages: MailboxEnvelope[];
  nextPageToken: string | null;
  skippedCount: number;
  externallyAuthored: true;
}

export const mailboxSearchOutputSchema = z.object({
  account: mailboxAccountSchema,
  messages: z.array(mailboxEnvelopeSchema),
  nextPageToken: z.string().nullable(),
  skippedCount: z.number().int().nonnegative(),
  externallyAuthored: z.literal(true),
});

export const mailboxReadMessageInputSchema = z
  .object({
    memberExternalAccountId: positiveIdSchema,
    providerMessageId: z.string().trim().min(1),
  })
  .strict();
export type MailboxReadMessageInput = z.infer<typeof mailboxReadMessageInputSchema>;

export const mailboxBodiesSchema = z.object({
  authoredBody: z.string().nullable(),
  fullReadableBody: z.string().nullable(),
  authoredTruncated: z.boolean(),
  fullTruncated: z.boolean(),
});
export type MailboxBodies = z.infer<typeof mailboxBodiesSchema>;

export const mailboxReadingStatsSchema = z.object({
  authoredChars: z.number().int().nonnegative(),
  fullChars: z.number().int().nonnegative(),
  authoredWordCount: z.number().int().nonnegative(),
  fullWordCount: z.number().int().nonnegative(),
  spokenMinutesAuthored: z.number().nonnegative(),
  spokenMinutesFull: z.number().nonnegative(),
  linkCount: z.number().int().nonnegative(),
});
export type MailboxReadingStats = z.infer<typeof mailboxReadingStatsSchema>;

export interface MailboxReadMessageResult {
  account: MailboxAccount;
  envelope: MailboxEnvelope;
  bodies: MailboxBodies;
  readingStats: MailboxReadingStats;
  externallyAuthored: true;
}

export const mailboxReadMessageOutputSchema = z.object({
  account: mailboxAccountSchema,
  envelope: mailboxEnvelopeSchema,
  bodies: mailboxBodiesSchema,
  readingStats: mailboxReadingStatsSchema,
  externallyAuthored: z.literal(true),
});

export const MAILBOX_THREAD_BODY_CAP_CHARS = 1_500;

export const mailboxReadThreadInputSchema = z
  .object({
    memberExternalAccountId: positiveIdSchema,
    providerThreadId: z.string().trim().min(1),
    maxMessages: z.number().int().min(1).max(50).default(20),
  })
  .strict();
export type MailboxReadThreadInput = z.infer<typeof mailboxReadThreadInputSchema>;

export const mailboxThreadMessageSchema = mailboxEnvelopeSchema.extend({
  authoredBody: z.string().nullable(),
  authoredTruncated: z.boolean(),
});
export type MailboxThreadMessage = z.infer<typeof mailboxThreadMessageSchema>;

export interface MailboxReadThreadResult {
  account: MailboxAccount;
  providerThreadId: string;
  messages: MailboxThreadMessage[];
  truncated: boolean;
  externallyAuthored: true;
}

export const mailboxReadThreadOutputSchema = z.object({
  account: mailboxAccountSchema,
  providerThreadId: z.string(),
  messages: z.array(mailboxThreadMessageSchema),
  truncated: z.boolean(),
  externallyAuthored: z.literal(true),
});

export const mailboxReadAttachmentInputSchema = z
  .object({
    memberExternalAccountId: positiveIdSchema,
    providerMessageId: z.string().trim().min(1),
    attachmentId: z.string().trim().min(1),
    maxChars: z.number().int().min(1).max(60_000).optional(),
    startChar: z.number().int().min(0).optional(),
  })
  .strict();
export type MailboxReadAttachmentInput = z.infer<typeof mailboxReadAttachmentInputSchema>;

export const MAILBOX_ATTACHMENT_READ_REASONS = ['OK', 'UNSUPPORTED_TYPE', 'TOO_LARGE', 'NO_TEXT_FOUND', 'NOT_FOUND'] as const;
export type MailboxAttachmentReadReason = (typeof MAILBOX_ATTACHMENT_READ_REASONS)[number];

export interface MailboxReadAttachmentResult {
  account: MailboxAccount;
  providerMessageId: string;
  attachmentId: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  reason: MailboxAttachmentReadReason;
  text: string | null;
  charCount: number;
  totalChars: number;
  truncated: boolean;
  externallyAuthored: true;
}

export const mailboxReadAttachmentOutputSchema = z.object({
  account: mailboxAccountSchema,
  providerMessageId: z.string(),
  attachmentId: z.string(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  reason: z.enum(MAILBOX_ATTACHMENT_READ_REASONS),
  text: z.string().nullable(),
  charCount: z.number().int().nonnegative(),
  totalChars: z.number().int().nonnegative(),
  truncated: z.boolean(),
  externallyAuthored: z.literal(true),
});

export const mailboxListFoldersInputSchema = z
  .object({
    memberExternalAccountId: positiveIdSchema,
  })
  .strict();
export type MailboxListFoldersInput = z.infer<typeof mailboxListFoldersInputSchema>;

export const mailboxFolderSchema = z.object({
  providerFolderId: z.string(),
  name: z.string(),
  kind: z.enum(['FOLDER', 'LABEL']),
  isSystem: z.boolean(),
});

export interface MailboxListFoldersResult {
  account: MailboxAccount;
  folders: MailboxFolder[];
  externallyAuthored: true;
}

export const mailboxListFoldersOutputSchema = z.object({
  account: mailboxAccountSchema,
  folders: z.array(mailboxFolderSchema),
  externallyAuthored: z.literal(true),
});

const queryListSchema = z
  .union([z.string(), z.array(z.string())])
  .transform((value) => (Array.isArray(value) ? value : [value]))
  .pipe(nameListSchema);

const queryBooleanSchema = z.preprocess((value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
}, z.boolean());

export const mailboxSearchQuerySchema = z
  .object({
    text: z.string().trim().min(1).optional(),
    anyOfText: queryListSchema.optional(),
    from: queryListSchema.optional(),
    to: queryListSchema.optional(),
    subject: z.string().trim().min(1).optional(),
    after: calendarDateSchema.optional(),
    before: calendarDateSchema.optional(),
    hasAttachment: queryBooleanSchema.optional(),
    unreadOnly: queryBooleanSchema.optional(),
    folderAnyOf: queryListSchema.optional(),
    folderNoneOf: queryListSchema.optional(),
    includeSpamTrash: queryBooleanSchema.optional(),
    timeZone: ianaTimeZoneSchema.optional(),
    maxResults: z.coerce.number().int().min(1).max(100).default(25),
    pageToken: z.string().trim().min(1).optional(),
  })
  .strict();
export type MailboxSearchRouteQuery = z.input<typeof mailboxSearchQuerySchema>;

export function toMailboxQueryFromRouteQuery(query: z.infer<typeof mailboxSearchQuerySchema>): MailboxQueryInput {
  const { folderAnyOf, folderNoneOf, maxResults: _maxResults, pageToken: _pageToken, ...rest } = query;
  const folders =
    folderAnyOf !== undefined || folderNoneOf !== undefined
      ? {
          ...(folderAnyOf !== undefined ? { anyOf: folderAnyOf } : {}),
          ...(folderNoneOf !== undefined ? { noneOf: folderNoneOf } : {}),
        }
      : undefined;
  return { ...rest, ...(folders !== undefined ? { folders } : {}) };
}

export const mailboxReadThreadQuerySchema = z
  .object({
    maxMessages: z.coerce.number().int().min(1).max(50).default(20),
  })
  .strict();
export type MailboxReadThreadRouteQuery = z.input<typeof mailboxReadThreadQuerySchema>;

export const mailboxReadAttachmentQuerySchema = z
  .object({
    maxChars: z.coerce.number().int().min(1).max(60_000).optional(),
    startChar: z.coerce.number().int().min(0).optional(),
  })
  .strict();
export type MailboxReadAttachmentRouteQuery = z.input<typeof mailboxReadAttachmentQuerySchema>;

export const mailboxImportInputSchema = z
  .object({
    memberExternalAccountId: positiveIdSchema,
    providerMessageIds: z
      .array(z.string().trim().min(1))
      .min(1)
      .max(50)
      .transform((ids) => [...new Set(ids)]),
    expandThreads: z.boolean().default(false),
    anchorId: positiveIdSchema.optional(),
    extractObligationsOnImport: z.boolean().optional(),
    autoFileOnImport: z.boolean().optional(),
  })
  .strict();
export type MailboxImportInput = z.infer<typeof mailboxImportInputSchema>;

export const mailboxImportBodySchema = mailboxImportInputSchema.omit({ memberExternalAccountId: true });
export type MailboxImportBody = z.input<typeof mailboxImportBodySchema>;

export const mailboxKeptMessageSchema = z.object({
  providerMessageId: z.string(),
  contentId: z.number().int(),
  code: z.string(),
});
export type MailboxKeptMessage = z.infer<typeof mailboxKeptMessageSchema>;

export const processRunRefSchema = z
  .object({
    contentId: z.number().int().positive(),
    code: z.string(),
  })
  .strict();

export interface MailboxImportResult {
  account: MailboxAccount;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  expandedThreadCount: number;
  createdContentIds: number[];
  updatedContentIds: number[];
  createdFileContentIds: number[];
  messageRootContentIds: number[];
  filedToAnchor: number;
  notFoundProviderMessageIds: string[];
  keptMessages: MailboxKeptMessage[];
  processRunRef?: { contentId: number; code: string };
}

export const mailboxImportOutputSchema = z.object({
  account: mailboxAccountSchema,
  importedCount: z.number().int().nonnegative(),
  updatedCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  expandedThreadCount: z.number().int().nonnegative(),
  createdContentIds: z.array(z.number().int()),
  updatedContentIds: z.array(z.number().int()),
  createdFileContentIds: z.array(z.number().int()),
  messageRootContentIds: z.array(z.number().int()),
  filedToAnchor: z.number().int().nonnegative(),
  notFoundProviderMessageIds: z.array(z.string()),
  keptMessages: z.array(mailboxKeptMessageSchema),
  processRunRef: processRunRefSchema.optional(),
});

export const mailboxLabelNameSchema = z.string().trim().min(1).max(100);

const createLabelBaseSchema = z
  .object({
    memberExternalAccountId: positiveIdSchema,
    name: mailboxLabelNameSchema,
  })
  .strict();
export const mailboxCreateLabelInputSchema = createLabelBaseSchema;
export type MailboxCreateLabelInput = z.infer<typeof mailboxCreateLabelInputSchema>;
export const mailboxCreateLabelBodySchema = createLabelBaseSchema.omit({ memberExternalAccountId: true });
export type MailboxCreateLabelBody = z.input<typeof mailboxCreateLabelBodySchema>;

export interface MailboxCreateLabelResult {
  account: MailboxAccount;
  label: MailboxFolder;
}

export const mailboxCreateLabelOutputSchema = z.object({
  account: mailboxAccountSchema,
  label: mailboxFolderSchema,
});

const applyLabelsBaseSchema = z
  .object({
    memberExternalAccountId: positiveIdSchema,
    providerMessageIds: z
      .array(z.string().trim().min(1))
      .min(1)
      .max(200)
      .transform((ids) => [...new Set(ids)]),
    add: z.array(mailboxLabelNameSchema).max(10).default([]),
    remove: z.array(mailboxLabelNameSchema).max(10).default([]),
    createMissing: z.boolean().default(false),
  })
  .strict();
const requireSomeLabel = (value: { add: string[]; remove: string[] }): boolean => value.add.length + value.remove.length > 0;
const requireSomeLabelOptions = { message: 'add or remove must name at least one label', path: ['add'] };
export const mailboxApplyLabelsInputSchema = applyLabelsBaseSchema.refine(requireSomeLabel, requireSomeLabelOptions);
export type MailboxApplyLabelsInput = z.infer<typeof mailboxApplyLabelsInputSchema>;
export const mailboxApplyLabelsBodySchema = applyLabelsBaseSchema
  .omit({ memberExternalAccountId: true })
  .refine(requireSomeLabel, requireSomeLabelOptions);
export type MailboxApplyLabelsBody = z.input<typeof mailboxApplyLabelsBodySchema>;

export interface MailboxApplyLabelsResult {
  account: MailboxAccount;
  appliedCount: number;
  createdLabels: MailboxFolder[];
  failedProviderMessageIds: string[];
}

export const mailboxApplyLabelsOutputSchema = z.object({
  account: mailboxAccountSchema,
  appliedCount: z.number().int().nonnegative(),
  createdLabels: z.array(mailboxFolderSchema),
  failedProviderMessageIds: z.array(z.string()),
});
